import supabase from "./supabaseClient";
import { ClaudeService } from "./claudeService";

const SESSION_COLUMNS = "id, room_id, building_id, kind, title, surface, last_surface, last_hotspot_id, status, token_count, created_at, updated_at";
export const LUMEN_TOKEN_CAP = 150000;
export const LUMEN_TOKEN_WARN = 0.8;
export const LUMEN_CARRY_MESSAGES = 4;
const TITLE_MODEL = "claude-haiku-4-5-20251001";
const TITLE_MAX_LENGTH = 60;

function isPersistable(message) {
  return Boolean(message && message.role && !message._buildingWorld);
}

export function toMessageRow(message) {
  const { role, content, isRestored, ...rest } = message;
  const meta = {};
  Object.keys(rest).forEach((key) => {
    if (!key.startsWith("_") && rest[key] !== undefined) meta[key] = rest[key];
  });
  return {
    role,
    content: content == null ? "" : typeof content === "string" ? content : JSON.stringify(content),
    meta,
  };
}

export function fromMessageRow(row) {
  return { ...(row.meta || {}), role: row.role, content: row.content };
}

function stableStringify(value) {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.keys(value).sort().map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`).join(",")}}`;
  }
  return JSON.stringify(value);
}

export function diffMessageRows(saved, rows) {
  const next = rows.map(stableStringify);
  const upserts = [];
  next.forEach((key, position) => {
    if (saved[position] !== key) upserts.push({ ...rows[position], position });
  });
  return { upserts, deleteFrom: saved.length > rows.length ? rows.length : null, saved: next };
}

export async function listLumenSessions({ userId, roomId, kind = "room" }) {
  const { data, error } = await supabase
    .from("lumen_sessions")
    .select(SESSION_COLUMNS)
    .eq("user_id", userId)
    .eq("room_id", roomId)
    .eq("kind", kind)
    .order("updated_at", { ascending: false });
  if (error) return { error, sessions: [] };
  return { sessions: data || [] };
}

export function capState(tokenCount, status) {
  const tokens = Number(tokenCount) || 0;
  if (status === "full" || tokens >= LUMEN_TOKEN_CAP) return "full";
  if (tokens >= LUMEN_TOKEN_CAP * LUMEN_TOKEN_WARN) return "warn";
  return "ok";
}

export function usageTokens(usage) {
  if (!usage) return 0;
  return (Number(usage.input_tokens) || 0)
    + (Number(usage.cache_read_input_tokens) || 0)
    + (Number(usage.cache_creation_input_tokens) || 0)
    + (Number(usage.output_tokens) || 0);
}

export function carryMessages(messages, count = LUMEN_CARRY_MESSAGES) {
  return (messages || [])
    .filter(isPersistable)
    .filter((m) => (m.role === "user" || m.role === "assistant") && !m.isWelcome && String(m.content || "").trim())
    .slice(-count)
    .map((m) => ({ role: m.role, content: m.content, isCarried: true }));
}

export function continuedTitle(title) {
  const base = cleanTitle(title);
  if (!base) return null;
  return cleanTitle(`Continued: ${base.replace(/^Continued: /, "")}`);
}

export function linkPair(a, b) {
  return a < b ? { session_id: a, related_session_id: b } : { session_id: b, related_session_id: a };
}

export function relatedIdsFor(links, sessionId) {
  return (links || []).flatMap((link) => {
    if (link.session_id === sessionId) return [link.related_session_id];
    if (link.related_session_id === sessionId) return [link.session_id];
    return [];
  });
}

export async function listSessionLinks({ userId, sessionIds }) {
  const ids = [...new Set((sessionIds || []).filter(Boolean))];
  if (ids.length === 0) return { links: [] };
  const list = ids.join(",");
  const { data, error } = await supabase
    .from("lumen_session_links")
    .select("session_id, related_session_id")
    .eq("user_id", userId)
    .or(`session_id.in.(${list}),related_session_id.in.(${list})`);
  if (error) return { error, links: [] };
  return { links: data || [] };
}

export async function setRelatedSessions({ userId, sessionId, relatedIds, links }) {
  const current = new Set(relatedIdsFor(links, sessionId));
  const wanted = new Set((relatedIds || []).filter((id) => id && id !== sessionId));
  const add = [...wanted].filter((id) => !current.has(id)).map((id) => ({ ...linkPair(sessionId, id), user_id: userId }));
  const drop = [...current].filter((id) => !wanted.has(id)).map((id) => linkPair(sessionId, id));

  if (add.length > 0) {
    const { error } = await supabase.from("lumen_session_links").upsert(add, { onConflict: "session_id,related_session_id", ignoreDuplicates: true });
    if (error) return { error };
  }
  for (const pair of drop) {
    const { error } = await supabase
      .from("lumen_session_links")
      .delete()
      .eq("user_id", userId)
      .eq("session_id", pair.session_id)
      .eq("related_session_id", pair.related_session_id);
    if (error) return { error };
  }
  const kept = (links || []).filter((link) => {
    const other = link.session_id === sessionId ? link.related_session_id : link.related_session_id === sessionId ? link.session_id : null;
    return other === null || wanted.has(other);
  });
  return { ok: true, links: [...kept, ...add.map(({ session_id, related_session_id }) => ({ session_id, related_session_id }))] };
}

export async function listBuildingSessions({ userId, roomIds }) {
  const ids = [...new Set((roomIds || []).filter(Boolean))];
  if (ids.length === 0) return { sessions: [] };
  const { data, error } = await supabase
    .from("lumen_sessions")
    .select(SESSION_COLUMNS)
    .eq("user_id", userId)
    .eq("kind", "room")
    .in("room_id", ids)
    .order("updated_at", { ascending: false });
  if (error) return { error, sessions: [] };
  return { sessions: data || [] };
}

export function upsertSessionRow(list, row) {
  const next = (list || []).filter((item) => item.id !== row.id);
  next.push(row);
  return next.sort((a, b) => String(b.updated_at).localeCompare(String(a.updated_at)));
}

export function mergeSessionRows(fetched, current) {
  const byId = new Map((fetched || []).map((row) => [row.id, row]));
  (current || []).forEach((row) => byId.set(row.id, { ...byId.get(row.id), ...row }));
  return [...byId.values()].sort((a, b) => String(b.updated_at).localeCompare(String(a.updated_at)));
}

export function readyForTitle(messages) {
  const list = (messages || []).filter(isPersistable);
  const firstUser = list.findIndex((m) => m.role === "user" && String(m.content || "").trim());
  return firstUser !== -1 && list.slice(firstUser + 1).some((m) => m.role === "assistant" && String(m.content || "").trim());
}

export function cleanTitle(raw) {
  const line = String(raw || "").split("\n").map((l) => l.trim()).find(Boolean) || "";
  const title = line.replace(/^title\s*:\s*/i, "").replace(/^["'`*#\s]+|["'`*.\s]+$/g, "").trim();
  return title.length > TITLE_MAX_LENGTH ? `${title.slice(0, TITLE_MAX_LENGTH - 1).trimEnd()}…` : title;
}

export async function generateLumenSessionTitle(messages, { roomName } = {}) {
  const transcript = (messages || [])
    .filter(isPersistable)
    .filter((m) => m.role === "user" || m.role === "assistant")
    .slice(0, 6)
    .map((m) => `${m.role === "user" ? "User" : "Lumen"}: ${String(m.content || "").slice(0, 600)}`)
    .join("\n");
  const firstUser = (messages || []).find((m) => m.role === "user" && String(m.content || "").trim());
  const fallback = cleanTitle(firstUser?.content);

  const result = await ClaudeService.sendMessage({
    model: TITLE_MODEL,
    maxTokens: 30,
    system: `You name chat sessions${roomName ? ` in a workspace room called "${roomName}"` : ""}. Reply with a 2 to 6 word title for the conversation, in sentence case, with no quotes and no ending punctuation. Name the topic, not the room.`,
    messages: [{ role: "user", content: transcript }],
  });
  const title = result?.success ? cleanTitle(result.content) : "";
  return title || fallback;
}

export function createLumenSessionSync({ userId, roomId, buildingId = null, kind = "room", onSessionChange }) {
  let session = null;
  let saved = [];
  let queue = Promise.resolve();
  let pendingTokens = 0;
  let pendingRelated = null;
  let pendingTitle = null;
  const titleRequested = new Set();

  const enqueue = (task) => {
    const run = queue.then(task, task);
    queue = run.catch(() => {});
    return run;
  };

  const notify = () => onSessionChange?.(session ? { ...session } : null);

  const reset = () => {
    session = null;
    saved = [];
    pendingTokens = 0;
    pendingRelated = null;
    pendingTitle = null;
  };

  const openRow = async (row) => {
    const { data: rows, error } = await supabase
      .from("lumen_messages")
      .select("position, role, content, meta")
      .eq("session_id", row.id)
      .order("position", { ascending: true });
    if (error) return { error };

    if (!row.building_id && buildingId) {
      const { error: backfillError } = await supabase
        .from("lumen_sessions")
        .update({ building_id: buildingId })
        .eq("id", row.id);
      if (!backfillError) row.building_id = buildingId;
    }

    const messages = (rows || []).map(fromMessageRow);
    session = row;
    saved = messages.map((m) => stableStringify(toMessageRow(m)));
    notify();
    return { session: { ...row }, messages };
  };

  const load = () => enqueue(async () => {
    const { data: latest, error } = await supabase
      .from("lumen_sessions")
      .select(SESSION_COLUMNS)
      .eq("user_id", userId)
      .eq("room_id", roomId)
      .eq("kind", kind)
      .order("updated_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    reset();
    if (error) return { error };
    if (!latest) {
      notify();
      return { session: null, messages: [] };
    }
    return openRow(latest);
  });

  const open = (sessionId) => enqueue(async () => {
    const { data: row, error } = await supabase
      .from("lumen_sessions")
      .select(SESSION_COLUMNS)
      .eq("id", sessionId)
      .eq("user_id", userId)
      .maybeSingle();
    if (error) return { error };
    if (!row) return { error: { message: "Session not found" } };
    return openRow(row);
  });

  const save = (messages, { surface = null, hotspotId = null } = {}) => {
    const rows = (messages || []).filter(isPersistable).map(toMessageRow);
    if (rows.length === 0) return Promise.resolve({ ok: true });

    return enqueue(async () => {
      if (!session) {
        const { data: created, error } = await supabase
          .from("lumen_sessions")
          .insert({
            user_id: userId, room_id: roomId, building_id: buildingId, kind, surface, last_surface: surface, last_hotspot_id: hotspotId, title: pendingTitle,
            token_count: pendingTokens, status: capState(pendingTokens) === "full" ? "full" : "active",
          })
          .select(SESSION_COLUMNS)
          .single();
        if (error) return { error };
        session = created;
        saved = [];
        pendingTokens = 0;
        pendingTitle = null;
        if (pendingRelated) {
          const relatedTo = pendingRelated;
          pendingRelated = null;
          const { error: linkError } = await supabase
            .from("lumen_session_links")
            .insert({ ...linkPair(created.id, relatedTo), user_id: userId });
          if (linkError) console.error("[Lumen] link error:", linkError.message);
        }
        notify();
      }

      const diff = diffMessageRows(saved, rows);
      if (diff.upserts.length === 0 && diff.deleteFrom === null) return { ok: true };

      if (diff.upserts.length > 0) {
        const { error } = await supabase
          .from("lumen_messages")
          .upsert(
            diff.upserts.map((row) => ({ ...row, session_id: session.id, user_id: userId })),
            { onConflict: "session_id,position" }
          );
        if (error) return { error };
      }
      if (diff.deleteFrom !== null) {
        const { error } = await supabase
          .from("lumen_messages")
          .delete()
          .eq("session_id", session.id)
          .gte("position", diff.deleteFrom);
        if (error) return { error };
      }
      saved = diff.saved;

      const changes = { updated_at: new Date().toISOString() };
      if (surface && surface !== session.last_surface) changes.last_surface = surface;
      if (hotspotId && hotspotId !== session.last_hotspot_id) changes.last_hotspot_id = hotspotId;
      const { error } = await supabase
        .from("lumen_sessions")
        .update(changes)
        .eq("id", session.id);
      if (error) return { error };
      session = { ...session, ...changes };
      notify();
      return { ok: true };
    });
  };

  const startNew = ({ relatedTo = null, title = null } = {}) => enqueue(async () => {
    reset();
    pendingRelated = relatedTo;
    pendingTitle = title ? cleanTitle(title) || null : null;
    notify();
  });

  const recordUsage = (usage) => {
    const tokens = usageTokens(usage);
    if (!tokens) return Promise.resolve({ skipped: true });
    return enqueue(async () => {
      if (!session) {
        pendingTokens = Math.max(pendingTokens, tokens);
        return { ok: true, pending: true };
      }
      const status = capState(tokens) === "full" ? "full" : session.status === "full" ? "full" : "active";
      const { error } = await supabase
        .from("lumen_sessions")
        .update({ token_count: tokens, status })
        .eq("id", session.id);
      if (error) return { error };
      session = { ...session, token_count: tokens, status };
      notify();
      return { ok: true };
    });
  };

  const rename = (sessionId, title) => enqueue(async () => {
    const clean = cleanTitle(title);
    if (!clean) return { error: { message: "Title is empty" } };
    const { error } = await supabase
      .from("lumen_sessions")
      .update({ title: clean })
      .eq("id", sessionId)
      .eq("user_id", userId);
    if (error) return { error };
    titleRequested.add(sessionId);
    if (session?.id === sessionId) {
      session = { ...session, title: clean };
      notify();
    }
    return { ok: true, title: clean };
  });

  const remove = (sessionId) => enqueue(async () => {
    const { error } = await supabase
      .from("lumen_sessions")
      .delete()
      .eq("id", sessionId)
      .eq("user_id", userId);
    if (error) return { error };
    if (session?.id === sessionId) {
      reset();
      notify();
    }
    return { ok: true };
  });

  const autoTitle = async (messages, generate = generateLumenSessionTitle) => {
    const target = session;
    if (!target || target.title || titleRequested.has(target.id) || !readyForTitle(messages)) return { skipped: true };
    titleRequested.add(target.id);

    let title = "";
    try {
      title = cleanTitle(await generate(messages));
    } catch (e) {
      title = "";
    }
    if (!title) return { skipped: true };

    return enqueue(async () => {
      const { data, error } = await supabase
        .from("lumen_sessions")
        .update({ title })
        .eq("id", target.id)
        .eq("user_id", userId)
        .is("title", null)
        .select("id");
      if (error) return { error };
      if (!data?.length) return { skipped: true };
      if (session?.id === target.id) {
        session = { ...session, title };
        notify();
      }
      return { ok: true, sessionId: target.id, title };
    });
  };

  return {
    load,
    open,
    save,
    startNew,
    recordUsage,
    rename,
    remove,
    autoTitle,
    get sessionId() { return session?.id ?? null; },
    get session() { return session ? { ...session } : null; },
  };
}
